import { useAuth as useClerkAuth, useClerk } from '@clerk/react';
import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';

import { debugAuth } from './debug';
import { isJwtExpiringSoon } from './jwt';

/**
 * AuthContext — Clerk-only. Thin wrapper that exposes a stable hook
 * surface for the rest of the app without re-implementing Clerk's
 * token lifecycle.
 *
 * Key decision: we do NOT cache the Clerk JWT in React state. Clerk's
 * own `getToken()` handles caching + refresh under the hood; layering
 * our own setInterval on top was the source of multiple production
 * bugs (#225, #232, plus a 401-storm sign-out loop fixed in PR after
 * #234). API call sites await `getIdToken()` right before each fetch
 * — there's no stale token to worry about and no refresh that can
 * miss a cycle.
 *
 * The surface intentionally avoids exposing the raw token: every
 * caller goes through `getIdToken()`. That way if we need to add
 * cross-cutting behavior (logging, metrics, error handling) we can
 * thread it through one chokepoint.
 */

interface AuthState {
  /**
   * True iff Clerk has finished loading. Until this flips true,
   * neither `isSignedIn` nor `userKey` are meaningful — gate any
   * redirect-on-unauthenticated logic on this.
   */
  isAuthReady: boolean;
  /** True iff the user has an active Clerk session. */
  isSignedIn: boolean;
  /**
   * Stable, non-secret cache key derived from the Clerk `userId`.
   * Use this in TanStack queryKey arrays. `null` when signed out.
   */
  userKey: string | null;
  /**
   * Fetch the current Clerk session JWT. Clerk handles caching +
   * refresh internally; this always returns a fresh-enough token
   * (or `null` if signed out / rotation failed). Each API call site
   * should `await getIdToken()` immediately before its fetch — never
   * cache the return value.
   */
  getIdToken: () => Promise<string | null>;
  /** Sign out and redirect to /sign-in. */
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const clerk = useClerk();
  const { isLoaded, isSignedIn, userId, getToken } = useClerkAuth();

  const getIdToken = useCallback(async (): Promise<string | null> => {
    // Clerk refreshes its short-lived session token on window refocus; in
    // that window `getToken()` can transiently return null or reject. Handing
    // back an empty bearer 401s the call (and fails saves / approvals mid-
    // work — INP-502). Force a fresh mint (`skipCache`) before giving up, so
    // a valid session always produces a usable token.
    try {
      const token = await getToken();
      // Force a fresh mint when there's no token OR the cached one is about to
      // expire — otherwise a mutation retry could resend the same stale bearer
      // and 401 again.
      if (token && !isJwtExpiringSoon(token)) return token;
      const fresh = await getToken({ skipCache: true });
      return fresh ?? token ?? null;
    } catch (err) {
      try {
        return (await getToken({ skipCache: true })) ?? null;
      } catch (retryErr) {
        // Genuinely no token (signed out / session revoked). Return null so
        // the call surfaces a clean 401 rather than an unhandled rejection.
        console.error('AuthContext: Clerk getToken failed after retry', retryErr);
        return null;
      }
    }
  }, [getToken]);

  const signOut = useCallback(() => {
    if (debugAuth()) {
      // eslint-disable-next-line no-console
      console.log('[auth.AuthContext] signOut() called', {
        from: typeof window !== 'undefined' ? window.location.pathname : null,
      });
    }
    void clerk.signOut({ redirectUrl: '/sign-in' });
  }, [clerk]);

  useEffect(() => {
    if (!debugAuth()) return;
    // eslint-disable-next-line no-console
    console.log('[auth.AuthContext] state', {
      isLoaded,
      isSignedIn,
      userId,
      path: typeof window !== 'undefined' ? window.location.pathname : null,
    });
  }, [isLoaded, isSignedIn, userId]);

  return (
    <AuthContext.Provider
      value={{
        isAuthReady: isLoaded,
        isSignedIn: isSignedIn ?? false,
        userKey: userId ?? null,
        getIdToken,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth used outside AuthProvider');
  return ctx;
}
